import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Language } from '@/lib/translations';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = localStorage.getItem('aimup-language');
    return (stored === 'en' ? 'en' : 'de') as Language;
  });

  // Load language from profile when user logs in
  useEffect(() => {
    if (!user?.id) return;
    const loadLang = async () => {
      const { data } = await supabase
        .from('profiles')
        .select('language')
        .eq('user_id', user.id)
        .maybeSingle();
      if (data?.language && (data.language === 'de' || data.language === 'en')) {
        setLanguageState(data.language as Language);
        localStorage.setItem('aimup-language', data.language);
      }
    };
    loadLang();
  }, [user?.id]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('aimup-language', lang);
    // Save to profile if logged in
    if (user?.id) {
      supabase
        .from('profiles')
        .update({ language: lang } as any)
        .eq('user_id', user.id)
        .then();
    }
  }, [user?.id]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};
